import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useState, memo } from 'react'
import {
  GraduationCap,
  Award,
  Sparkles,
  CheckCircle,
  ChevronRight,
  BookOpen,
  Target,
  TrendingUp,
  Heart,
  Zap,
  Globe,
  ArrowRight,
} from 'lucide-react'
import { ContactCTA } from '@/components/sections'
import formazioneImg from '@/assets/images/sections/FormazioneBF.jpeg'

// Course data with workflow steps
const courses = [
  {
    id: 'wwwork',
    icon: Sparkles,
    color: 'from-emerald-400 to-teal-500',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    accentColor: 'text-emerald-600',
  },
  {
    id: 'wellness_tourism',
    icon: Globe,
    color: 'from-teal-500 to-emerald-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    accentColor: 'text-teal-600',
  },
  {
    id: 'spa_manager',
    icon: Award,
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    accentColor: 'text-emerald-600',
  },
  {
    id: 'sensorial_massage',
    icon: Heart,
    color: 'from-teal-600 to-emerald-700',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    accentColor: 'text-teal-600',
  },
]

// Memoized Course Card Component
const CourseCard = memo(function CourseCard({
  course,
  index,
  isActive,
  onClick,
  t,
}: {
  course: typeof courses[0]
  index: number
  isActive: boolean
  onClick: () => void
  t: (key: string, options?: object) => string | string[]
}) {
  const Icon = course.icon

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      onClick={onClick}
      className={`relative group w-full text-left p-3 sm:p-4 rounded-lg sm:rounded-xl overflow-hidden border border-white/50 transition-all duration-200 ${
        isActive
          ? `${course.bgColor} shadow-lg`
          : 'bg-white/80 backdrop-blur-sm shadow-md hover:shadow-lg'
      }`}
    >
      {/* Background gradient orb for active */}
      {isActive && (
        <div
          className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${course.color} opacity-20 blur-2xl`}
        />
      )}

      <div className="relative flex items-center gap-2 sm:gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-md sm:rounded-lg bg-gradient-to-br ${course.color} flex items-center justify-center shadow-sm`}
        >
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-tight">
            {t(`courses.${course.id}.title`) as string}
          </h3>
          <p className="text-xs sm:text-sm text-gray-600">
            {t(`courses.${course.id}.subtitle`) as string}
          </p>
        </div>

        {/* Arrow */}
        <ChevronRight
          className={`flex-shrink-0 w-5 h-5 transition-colors duration-200 ${
            isActive ? course.accentColor : 'text-gray-400 group-hover:text-gray-600'
          }`}
        />
      </div>
    </motion.button>
  )
})

// Workflow Step Component - Balanced version
const WorkflowStep = memo(function WorkflowStep({
  step,
  index,
  total,
  color,
}: {
  step: { title: string; description: string }
  index: number
  total: number
  color: string
  accentColor: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.08 }}
      className="relative flex gap-3"
    >
      {/* Timeline */}
      <div className="flex flex-col items-center">
        {/* Step number circle */}
        <div
          className={`relative z-10 w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br ${color} flex items-center justify-center shadow-sm`}
        >
          <span className="text-white font-bold text-xs sm:text-sm">{index + 1}</span>
        </div>

        {/* Connecting line */}
        {index < total - 1 && (
          <div className={`w-0.5 flex-1 bg-gradient-to-b ${color} opacity-30 mt-1`} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-3 sm:pb-4">
        <div className="bg-white rounded-md sm:rounded-lg p-2.5 sm:p-3 shadow-sm border border-gray-100">
          <h4 className="font-semibold text-gray-900 text-xs sm:text-sm mb-0.5">{step.title}</h4>
          <p className="text-gray-600 text-[10px] sm:text-xs leading-relaxed">{step.description}</p>
        </div>
      </div>
    </motion.div>
  )
})

// BF Academy Badge
const AcademyBadge = memo(function AcademyBadge() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-full shadow-lg"
    >
      <GraduationCap className="w-5 h-5" />
      <span className="font-semibold tracking-wide">BF ACADEMY</span>
    </motion.div>
  )
})

// Method pillars visualization
const MethodPillars = memo(function MethodPillars({ t }: { t: (key: string) => string }) {
  const pillars = [
    { key: 'inform', icon: BookOpen, color: 'from-emerald-400 to-teal-500' },
    { key: 'train', icon: Target, color: 'from-emerald-500 to-teal-600' },
    { key: 'recruit', icon: TrendingUp, color: 'from-teal-500 to-emerald-600' },
  ]

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {pillars.map((pillar, index) => {
        const Icon = pillar.icon
        return (
          <motion.div
            key={pillar.key}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            className="relative group"
          >
            {/* Glassmorphism card */}
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur-sm border border-white/50 p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
              {/* Background gradient orb */}
              <div
                className={`absolute -top-10 -right-10 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br ${pillar.color} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`}
              />

              {/* Icon */}
              <div
                className={`relative w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mb-3 sm:mb-4 shadow-lg`}
              >
                <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">
                {t(`method.pillars.${pillar.key}.title`)}
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {t(`method.pillars.${pillar.key}.description`)}
              </p>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
})

export function Training() {
  const { t, i18n } = useTranslation('training')
  const [activeCourse, setActiveCourse] = useState(0)

  // Get workflow steps for active course
  const workflowSteps = t(`courses.${courses[activeCourse].id}.workflow`, {
    returnObjects: true,
  }) as { title: string; description: string }[]

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      {/* Hero Section with Parallax Effect */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={formazioneImg}
            alt="BF Academy Training"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-64 h-64 rounded-full bg-white/5"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.2, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }}
              transition={{
                duration: 8 + i * 2,
                repeat: Infinity,
                delay: i * 1.5,
              }}
              style={{
                left: `${20 + i * 15}%`,
                top: `${10 + i * 20}%`,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative container-custom py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <AcademyBadge />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-white mt-6 mb-6"
            >
              {t('hero.title')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* Method Keywords */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {['INFORMARVI', 'FORMARVI', 'RECLUTARVI'].map((word) => (
                <span
                  key={word}
                  className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold text-sm"
                >
                  {word}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 rounded-full bg-white/60" />
          </motion.div>
        </motion.div>
      </section>

      {/* Method Section - INFORMARVI, FORMARVI, RECLUTARVI */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 opacity-50 blur-3xl -translate-y-1/2 translate-x-1/2" />

        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('method.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('method.subtitle')}</p>
          </motion.div>

          <MethodPillars t={t as (key: string) => string} />
        </div>
      </section>

      {/* Interactive Course Selector with Workflow */}
      <section className="py-10 md:py-14 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {t('courses.title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('courses.subtitle')}</p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-4 lg:gap-6">
            {/* Course Cards - Left Side */}
            <div className="lg:col-span-2 space-y-2 sm:space-y-3">
              {courses.map((course, index) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  index={index}
                  isActive={activeCourse === index}
                  onClick={() => setActiveCourse(index)}
                  t={t as (key: string, options?: object) => string | string[]}
                />
              ))}
            </div>

            {/* Workflow Visualization - Right Side */}
            <div className="lg:col-span-3">
              <motion.div
                key={activeCourse}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className={`${courses[activeCourse].bgColor} rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6 h-full`}
              >
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
                  <div
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-md sm:rounded-lg bg-gradient-to-br ${courses[activeCourse].color} flex items-center justify-center`}
                  >
                    <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-900">
                      {t(`courses.${courses[activeCourse].id}.workflow_title`)}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">{t('courses.workflow_description')}</p>
                  </div>
                </div>

                {/* Workflow Steps */}
                <div className="space-y-1">
                  {Array.isArray(workflowSteps) &&
                    workflowSteps.map((step, index) => (
                      <WorkflowStep
                        key={index}
                        step={step}
                        index={index}
                        total={workflowSteps.length}
                        color={courses[activeCourse].color}
                        accentColor={courses[activeCourse].accentColor}
                      />
                    ))}
                </div>

                {/* CTA */}
                <motion.a
                  href="#contact"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${courses[activeCourse].color} text-white text-sm font-semibold shadow-md hover:shadow-lg transition-shadow`}
                >
                  {t('courses.cta')}
                  <ArrowRight className="w-3.5 h-3.5" />
                </motion.a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('why.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-8">{t('why.description')}</p>

              <div className="space-y-4">
                {(t('why.points', { returnObjects: true }) as string[]).map((point, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{point}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={formazioneImg}
                  alt="BF Wellness Training"
                  className="w-full h-auto"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent" />
              </div>

              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-xl border border-gray-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{t('why.badge.title')}</div>
                    <div className="text-sm text-gray-600">{t('why.badge.subtitle')}</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  )
}
